import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { ROUTES } from 'config/navConfig';
import { useRole } from 'context/RoleContext';
import { ROLES, isExternalRole } from 'config/roleConfig';
import ForgotPasswordModal from './components/ForgotPasswordModal';

const LOGO = '/assets/images/SeedDrive_Black_-1772706977187.png';

// Five representative demo personas covering the main journey types
const DEMO_CREDENTIALS = [
  { label: 'Investment Team',   email: 'investment.team@greenfieldfunds.demo', role: ROLES.INVESTMENT_TEAM,   contextId: 'ctx-investment-team' },
  { label: 'IC Member',         email: 'ic.member@greenfieldfunds.demo',       role: ROLES.IC_MEMBER,         contextId: 'ctx-ic-member' },
  { label: 'Asset Management',  email: 'asset.mgmt@greenfieldfunds.demo',      role: ROLES.ASSET_MGMT_TEAM,   contextId: 'ctx-asset-mgmt-team' },
  { label: 'External Investor', email: 'investor@seeddrive.demo',              role: ROLES.EXTERNAL_INVESTOR, contextId: 'ctx-external-investor' },
  { label: 'Co-Investor',       email: 'coinvestor@seeddrive.demo',            role: ROLES.CO_INVESTOR,       contextId: 'ctx-co-investor' },
];

const VALID_EMAILS = DEMO_CREDENTIALS.map((c) => c.email);
const DEMO_PASSWORD = 'SeedDriveDemo1';

const TRUST_POINTS = [
  { icon: 'Building2',    text: 'Institutional-grade deal flow with full audit trail' },
  { icon: 'ShieldCheck',  text: 'Regulated digital rails — FCA, AIFMD & GDPR aligned' },
  { icon: 'Layers',       text: 'End-to-end SPV structuring and tokenisation pathway' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { setContext } = useRole();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  // Route by role type after login
  const routeAfterLogin = (role) => {
    if (role === ROLES.CO_INVESTOR) {
      navigate(ROUTES.spvDashboard);
    } else if (isExternalRole(role)) {
      navigate(ROUTES.opportunities);
    } else {
      navigate(ROUTES.dashboard);
    }
  };

  const handleSignIn = (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const trimmedEmail = email?.toLowerCase()?.trim();
      const validPassword = password === DEMO_PASSWORD;
      const matchedCred = DEMO_CREDENTIALS.find((c) => c.email === trimmedEmail);
      if (matchedCred && validPassword) {
        setContext(matchedCred.contextId);
        showToast('Welcome back — signed in successfully');
        setTimeout(() => routeAfterLogin(matchedCred.role), 800);
      } else {
        setError('Invalid email or password. Use a demo credential below.');
      }
    }, 900);
  };

  const handleDemoLogin = (cred) => {
    setEmail(cred.email);
    setPassword(DEMO_PASSWORD);
    setError('');
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setContext(cred.contextId);
      showToast(`Signed in as ${cred.label}`);
      setTimeout(() => routeAfterLogin(cred.role), 800);
    }, 700);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Branding */}
      <div
        className="hidden lg:flex lg:w-2/5 flex-col justify-between p-10 xl:p-14"
        style={{ background: 'linear-gradient(135deg, #1a2f2f 0%, #243d3d 40%, #162828 100%)' }}
      >
        {/* Logo */}
        <Link to={ROUTES.home} className="flex items-center group">
          <div className="bg-white rounded-xl px-3 py-2 inline-flex">
            <img
              src={LOGO}
              alt="SeedDrive logo"
              style={{ height: '36px', width: 'auto' }}
              className="object-contain"
            />
          </div>
        </Link>

        {/* Centre content */}
        <div className="space-y-8">
          <div>
            <p className="text-xs font-caption font-semibold uppercase tracking-widest mb-3" style={{ color: 'rgba(224,224,224,0.55)' }}>
              Institutional Platform
            </p>
            <h2 className="font-heading text-3xl xl:text-4xl font-semibold text-white leading-tight">
              Infrastructure equity,<br />structured for institutions.
            </h2>
          </div>
          <div className="space-y-4">
            {TRUST_POINTS.map((pt) => (
              <div key={pt.text} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <Icon name={pt.icon} size={16} color="rgba(224,224,224,0.80)" />
                </div>
                <p className="text-sm font-caption leading-relaxed" style={{ color: 'rgba(242,240,236,0.75)' }}>{pt.text}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs font-caption" style={{ color: 'rgba(224,224,224,0.40)' }}>
          &copy; {new Date().getFullYear()} SeedDrive. All rights reserved.
        </p>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12" style={{ background: '#F2F0EC' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center mb-8 lg:hidden">
            <img src={LOGO} alt="SeedDrive logo" style={{ height: '34px', width: 'auto' }} className="object-contain" />
          </div>

          <div className="mb-8">
            <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-1">Sign in to your account</h1>
            <p className="text-sm text-slate-500 font-caption">Access your institutional dashboard and deal flow.</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <form onSubmit={handleSignIn} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 font-caption mb-1.5">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@institution.com"
                  required
                  className="w-full px-4 py-2.5 border border-slate-200 text-sm font-caption text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20 focus:border-transparent transition-all"
                  style={{ borderRadius: 0 }}
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 font-caption mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="Enter your password"
                    required
                    className="w-full px-4 py-2.5 pr-11 border border-slate-200 text-sm font-caption text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20 focus:border-transparent transition-all"
                    style={{ borderRadius: 0 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 accent-slate-800 cursor-pointer"
                />
                <label htmlFor="remember" className="text-sm text-slate-600 font-caption cursor-pointer select-none">Remember me</label>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200">
                  <Icon name="AlertCircle" size={15} color="#dc2626" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs font-caption text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-sm font-semibold font-caption text-white transition-all duration-200 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: '#3b4c4c', borderRadius: 0 }}
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  className="text-xs font-caption font-medium transition-colors hover:underline"
                  style={{ color: 'var(--color-primary, #3b4c4c)' }}
                >
                  Forgot password?
                </button>
              </div>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-caption" style={{ color: '#4f6363' }}>demo access</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Demo login buttons — 4 key personas */}
            <div className="grid grid-cols-2 gap-2">
              {DEMO_CREDENTIALS.map((cred) => (
                <button
                  key={cred.role}
                  type="button"
                  onClick={() => handleDemoLogin(cred)}
                  disabled={loading}
                  className="px-3 py-2 text-xs font-semibold font-caption transition-all duration-200 disabled:opacity-50 text-left"
                  style={{ background: '#1e2b2b', color: '#F2F0EC', borderRadius: 0 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#3b4c4c'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#1e2b2b'; }}
                >
                  {cred.label}
                </button>
              ))}
            </div>

            {/* Demo note */}
            <p className="mt-4 text-center text-xs font-caption text-slate-400 leading-relaxed">
              Demo only · Password: <span className="font-mono">SeedDriveDemo1</span><br />
              Internal roles → Dashboard · External roles → Marketplace
            </p>
          </div>

          <p className="mt-6 text-center text-sm font-caption text-slate-500">
            Don't have an account?{' '}
            <Link
              to={ROUTES.investorOnboarding}
              className="font-semibold hover:underline transition-colors"
              style={{ color: 'var(--color-primary, #3b4c4c)' }}
            >
              Request access
            </Link>
          </p>
        </div>
      </div>

      {showForgot && (
        <ForgotPasswordModal
          onClose={() => setShowForgot(false)}
          onToast={showToast}
        />
      )}

      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-white text-sm font-caption font-medium"
          style={{ background: 'var(--color-primary, #3b4c4c)', minWidth: '280px' }}
        >
          <Icon name="CheckCircle" size={17} color="rgba(224,224,224,0.80)" />
          {toast}
        </div>
      )}
    </div>
  );
}
